import React, { useEffect, useState } from "react";
import { Button, Card, Progress } from "antd";
import { CheckCircleFilled, CloseOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BASE_URL } from "../../Constants";

const dismissKey = (eventId) => `guestChecklistDismissed:${eventId}`;

/**
 * "What to do next" card at the top of a wedding's guest list. Four steps that tick themselves
 * from real data (guests added, invitations sent, replies in, entry gates set up) and each one
 * has a button for the action. Once the main steps are done the host can close it.
 */
const NextStepsChecklist = ({
  eventId,
  total,
  pending,
  hasGates,
  refreshKey,
  onAdd,
  onSend,
  onShowWaiting,
  onGates,
  primaryColor,
  isMobile,
}) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  // Collapsed by default - the host opens it when they want the to-do list.
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey(eventId)) === "1";
    } catch {
      return false;
    }
  });

  // Invitation counts change whenever guests are added or invitations are sent.
  useEffect(() => {
    if (!eventId || dismissed) return;
    axios
      .get(`${BASE_URL}/EventWallet/invitation-summary/${eventId}`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [eventId, refreshKey, dismissed]);

  if (dismissed) return null;

  const addDone = total > 0;
  const toSend = summary ? (summary.notSent || 0) + (summary.failed || 0) : 0;
  const sendDone = addDone && !!summary && toSend === 0;
  const repliesDone = addDone && sendDone && pending === 0;

  const steps = [
    {
      key: "add",
      done: addDone,
      title: t("eventRegistrations.checklist.addTitle"),
      sub: addDone ? t("eventRegistrations.checklist.addDone", { count: total }) : t("eventRegistrations.checklist.addSub"),
      action: t("eventRegistrations.checklist.addAction"),
      onClick: onAdd,
    },
    {
      key: "send",
      done: sendDone,
      title: t("eventRegistrations.checklist.sendTitle"),
      sub: sendDone
        ? t("eventRegistrations.checklist.sendDone")
        : addDone && summary
          ? t("eventRegistrations.checklist.sendSub", { count: toSend })
          : t("eventRegistrations.checklist.sendWait"),
      action: t("eventRegistrations.checklist.sendAction"),
      onClick: onSend,
      disabled: !addDone,
    },
    {
      key: "replies",
      done: repliesDone,
      title: t("eventRegistrations.checklist.repliesTitle"),
      sub: repliesDone
        ? t("eventRegistrations.checklist.repliesDone")
        : addDone && sendDone
          ? t("eventRegistrations.checklist.repliesSub", { count: pending })
          : t("eventRegistrations.checklist.repliesWait"),
      action: t("eventRegistrations.checklist.repliesAction"),
      onClick: onShowWaiting,
      disabled: !addDone || !sendDone,
    },
    {
      key: "gates",
      done: hasGates,
      optional: true,
      title: t("eventRegistrations.checklist.gatesTitle"),
      sub: hasGates ? t("eventRegistrations.checklist.gatesDone") : t("eventRegistrations.checklist.gatesSub"),
      action: t("eventRegistrations.checklist.gatesAction"),
      onClick: onGates,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const mainDone = steps.slice(0, 3).every((s) => s.done);
  // The one thing to do now: the first main step that is not done. Shown even while the list is collapsed.
  const nextStep = steps.slice(0, 3).find((s) => !s.done && !s.disabled);

  const close = () => {
    try {
      localStorage.setItem(dismissKey(eventId), "1");
    } catch {
      /* closing still works for this visit */
    }
    setDismissed(true);
  };

  return (
    <Card size={isMobile ? "small" : "default"} style={{ marginBottom: 20 }} styles={{ body: { padding: isMobile ? 12 : 20 } }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setExpanded((v) => !v);
          }}
          style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", minWidth: 0 }}
        >
          <div style={{ fontWeight: 600, fontSize: 16 }}>{t("eventRegistrations.checklist.title")}</div>
          <span style={{ fontSize: 12, color: "#8c8c8c" }}>
            {t("eventRegistrations.checklist.progress", { done: doneCount, total: steps.length })}
          </span>
          <span style={{ marginInlineStart: "auto", color: "#8c8c8c" }}>
            {expanded ? <UpOutlined /> : <DownOutlined />}
          </span>
        </div>
        {mainDone && (
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={close} aria-label={t("eventRegistrations.checklist.close")} />
        )}
      </div>

      {!expanded && nextStep && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{nextStep.title}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>{nextStep.sub}</div>
          </div>
          <Button
            size="small"
            type="primary"
            onClick={nextStep.onClick}
            style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
          >
            {nextStep.action}
          </Button>
        </div>
      )}

      {expanded && (
        <>
      <Progress
        percent={Math.round((doneCount / steps.length) * 100)}
        steps={steps.length}
        size="small"
        strokeColor={primaryColor}
        showInfo={false}
        style={{ margin: "6px 0 10px" }}
      />

      <div style={{ display: "flex", flexDirection: "column" }}>
        {steps.map((s, i) => (
          <div
            key={s.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 0",
              borderTop: i === 0 ? "none" : "1px solid #f5f5f5",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            {s.done ? (
              <CheckCircleFilled style={{ fontSize: 22, color: "#52c41a", flexShrink: 0 }} />
            ) : (
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: `1.5px solid ${primaryColor}`,
                  color: primaryColor,
                  fontSize: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </span>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>
                {s.title}
                {s.optional && (
                  <span style={{ fontWeight: 400, fontSize: 12, color: "#8c8c8c", marginInlineStart: 6 }}>
                    {t("eventRegistrations.checklist.optional")}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>{s.sub}</div>
            </div>
            {!s.done && (
              <Button
                size="small"
                type={s.disabled ? "default" : "primary"}
                disabled={s.disabled}
                onClick={s.onClick}
                style={s.disabled ? undefined : { backgroundColor: primaryColor, borderColor: primaryColor }}
              >
                {s.action}
              </Button>
            )}
          </div>
        ))}
      </div>
        </>
      )}
    </Card>
  );
};

export default NextStepsChecklist;
